// Local request id helper (keep consistent with useFetchApi.ts)
const generateRequestId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getAuthToken = async (): Promise<string> => {
  if (import.meta.server) {
    return useCookie<string>("authToken", {}).value || "";
  }

  return "";
};

type FetchApiWithTokenOptions<T> = {
  method?: string;
  headers?: Record<string, string>;
  query?: Record<string, any>;
  params?: Record<string, any>;
  body?: T extends any ? any : any;
  requireAuth?: boolean;
};

/**
 * Promise-based $fetch wrapper that ENFORCES auth token presence (unless requireAuth=false).
 * - Gets token via cookie on server
 * - Uses a same-origin server proxy on client so HTTP-only authToken stays server-side
 * - Retries once after refresh if backend returns 401
 */
export const useFetchApiWithToken = () => {
  const fetchWithToken = async <TResponse = any>(
    url: string,
    options: FetchApiWithTokenOptions<any> = {}
  ): Promise<TResponse> => {
    const requireAuth = options.requireAuth ?? true;

    let authToken = "";
    if (requireAuth && import.meta.server) {
      authToken = await getAuthToken();
      if (!authToken) {
        // During initial page load / redirects the healthcheck header can be temporarily unavailable.
        // If the app still considers the user authenticated, try a refresh once before forcing logout.
        const isAuthenticated = !!useCookie<string>("isAuthenticated", {}).value;
        if (isAuthenticated) {
          try {
            await getNewToken("missing-token");
          } catch {
            // ignore; we will handle below
          }
          authToken = await getAuthToken();
        }

        // Only trigger the global unauthorised flow if we *expected* to be authenticated.
        if (isAuthenticated && !authToken) {
          await useUnAuthorize("missing-token");
        }

        if (!authToken) {
          throw new Error("Missing auth token");
        }
      }
    }

    const headers: Record<string, string> = {
      ...(options.headers || {}),
      "X-Request-Id": generateRequestId(),
    };

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    if (requireAuth && import.meta.client) {
      return await fetchViaAuthProxy<TResponse>(url, {
        method: options.method,
        body: (options as any).body,
        query: options.query ?? options.params,
        headers,
      });
    }

    const fetchOptions: any = {
      method: options.method,
      body: (options as any).body,
      query: options.query ?? options.params,
      headers,
    };

    try {
      return (await $fetch<TResponse>(url, fetchOptions as any)) as TResponse;
    } catch (error: any) {
      if (error?.response?.status === 401 && requireAuth) {
        const ctx = useCookie<string>("isAuthenticated", {}).value;
        const refreshed = await getNewToken(`on-resp-${ctx}`);
        if (refreshed) {
          const retriedToken = await getAuthToken();
          if (retriedToken) {
            const retryHeaders = {
              ...headers,
              Authorization: `Bearer ${retriedToken}`,
              "X-Request-Id": generateRequestId(),
            };
            try {
              return (await $fetch<TResponse>(
                url,
                ({ ...fetchOptions, headers: retryHeaders } as any)
              )) as TResponse;
            } catch (retryError: any) {
              if (retryError?.response?.status === 401) {
                await useUnAuthorize("retry-401");
              }
              throw retryError;
            }
          }
        }

        await useUnAuthorize("401");
      }

      if (error?.response?._data?.errors) {
        useShowValidationToasts(error.response._data.errors);
      }

      throw error;
    }
  };

  return { fetchWithToken };
};

async function fetchViaAuthProxy<TResponse>(
  url: string,
  options: {
    method?: string;
    headers?: Record<string, string>;
    query?: Record<string, any>;
    body?: any;
  },
): Promise<TResponse> {
  try {
    return await $fetch<TResponse>("/api/auth/proxy", {
      method: "POST",
      body: {
        url,
        ...options,
      },
    });
  } catch (error: any) {
    if (error?.response?.status === 401) {
      await getNewToken("proxy-401");
      try {
        return await $fetch<TResponse>("/api/auth/proxy", {
          method: "POST",
          body: {
            url,
            ...options,
            headers: {
              ...options.headers,
              "X-Request-Id": generateRequestId(),
            },
          },
        });
      } catch (retryError: any) {
        if (retryError?.response?.status === 401) {
          await useUnAuthorize("retry-401");
        }
        throw retryError;
      }
    }

    throw error;
  }
}
